import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { Switch } from './ui/switch';
import { AlertTriangle, Save, Loader2, Phone } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ALL_CATEGORIES = [
  { value: 'VET', label: 'Vet' },
  { value: 'FARRIER', label: 'Farrier' },
  { value: 'HORSE_HAULING', label: 'Hauling' },
  { value: 'PREDATOR_CONTROL', label: 'Predator Control' },
  { value: 'FENCE_REPAIR', label: 'Fence Repair' },
  { value: 'EQUIPMENT_REPAIR', label: 'Equipment Repair' },
  { value: 'ANIMAL_INJURY', label: 'Animal Injury' },
  { value: 'OTHER', label: 'Other' },
];

/**
 * Emergency Service Finder settings — a focused card the provider sees on
 * their Settings page. Independent of cancellation/profile settings so it
 * can be loaded/saved without touching the rest.
 */
export default function EmergencySettingsCard() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [offersEmergencyService, setOffersEmergencyService] = useState(false);
  const [emergencyCategories, setEmergencyCategories] = useState([]);
  const [available24hr, setAvailable24hr] = useState(false);
  const [currentlyAvailable, setCurrentlyAvailable] = useState(false);
  const [emergencyPhone, setEmergencyPhone] = useState('');
  const [emergencyRadiusMiles, setEmergencyRadiusMiles] = useState('');
  const [emergencyNotes, setEmergencyNotes] = useState('');

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/dashboard/settings`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!mounted) return;
        const d = res.data || {};
        setOffersEmergencyService(!!d.offersEmergencyService);
        setEmergencyCategories(d.emergencyCategories || []);
        setAvailable24hr(!!d.available24hr);
        setCurrentlyAvailable(!!d.currentlyAvailable);
        setEmergencyPhone(d.emergencyPhone || '');
        setEmergencyRadiusMiles(
          d.emergencyRadiusMiles != null ? String(d.emergencyRadiusMiles) : ''
        );
        setEmergencyNotes(d.emergencyNotes || '');
      } catch (e) {
        // silent
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [token]);

  const toggleCategory = (value) => {
    setEmergencyCategories((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        offersEmergencyService,
        emergencyCategories,
        available24hr,
        currentlyAvailable,
        emergencyPhone: emergencyPhone || null,
        emergencyRadiusMiles: emergencyRadiusMiles ? parseInt(emergencyRadiusMiles, 10) : null,
        emergencyNotes: emergencyNotes || null,
      };
      await axios.patch(`${API_URL}/api/dashboard/emergency-settings`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Emergency settings saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid="emergency-settings-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-red-600" />
          Emergency Service Finder
        </CardTitle>
        <CardDescription>
          Opt in to appear in the public Emergency Service Finder. RanchWorks does not dispatch
          help — clients call or message you directly.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="flex justify-center py-4">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            {/* Master toggle */}
            <div className="flex items-center justify-between p-3 rounded-lg border bg-muted/30">
              <div>
                <Label className="font-medium">Offer emergency service</Label>
                <p className="text-xs text-muted-foreground">
                  When off, you won't appear in the Emergency Service Finder.
                </p>
              </div>
              <Switch
                checked={offersEmergencyService}
                onCheckedChange={setOffersEmergencyService}
                data-testid="offers-emergency-toggle"
              />
            </div>

            {offersEmergencyService && (
              <div className="space-y-4">
                {/* Categories */}
                <div>
                  <Label>Categories you cover</Label>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    {ALL_CATEGORIES.map((c) => (
                      <label
                        key={c.value}
                        className="flex items-center gap-2 p-2 rounded border cursor-pointer hover:bg-muted text-sm"
                      >
                        <Checkbox
                          checked={emergencyCategories.includes(c.value)}
                          onCheckedChange={() => toggleCategory(c.value)}
                          data-testid={`emergency-cat-${c.value}`}
                        />
                        {c.label}
                      </label>
                    ))}
                  </div>
                </div>

                {/* Availability flags */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex items-center justify-between p-3 rounded border">
                    <div>
                      <Label>24-hour</Label>
                      <p className="text-xs text-muted-foreground">Around-the-clock coverage</p>
                    </div>
                    <Switch
                      checked={available24hr}
                      onCheckedChange={setAvailable24hr}
                      data-testid="available-24hr-toggle"
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 rounded border">
                    <div>
                      <Label>On call now</Label>
                      <p className="text-xs text-muted-foreground">Highlights you in results</p>
                    </div>
                    <Switch
                      checked={currentlyAvailable}
                      onCheckedChange={setCurrentlyAvailable}
                      data-testid="currently-available-toggle"
                    />
                  </div>
                </div>

                {/* Emergency phone + radius */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Emergency phone (optional)</Label>
                    <div className="relative mt-1">
                      <Phone className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                      <Input
                        value={emergencyPhone}
                        onChange={(e) => setEmergencyPhone(e.target.value)}
                        placeholder="Same as profile if blank"
                        className="pl-7"
                        maxLength={30}
                        data-testid="emergency-phone-input"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Emergency service radius (miles)</Label>
                    <Input
                      type="number"
                      min="0"
                      max="1000"
                      value={emergencyRadiusMiles}
                      onChange={(e) => setEmergencyRadiusMiles(e.target.value)}
                      placeholder="Defaults to your service radius"
                      data-testid="emergency-radius-input"
                    />
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <Label>Notes for emergency clients (optional)</Label>
                  <Textarea
                    rows={2}
                    value={emergencyNotes}
                    onChange={(e) => setEmergencyNotes(e.target.value)}
                    placeholder="What clients should know — fees, response time, area coverage…"
                    maxLength={1000}
                    data-testid="emergency-notes-input"
                  />
                </div>
              </div>
            )}

            <Button onClick={save} disabled={saving} data-testid="emergency-save-btn">
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin mr-1" />
              ) : (
                <Save className="h-4 w-4 mr-1" />
              )}
              Save Emergency Settings
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}
